/**
 * Cloud-safe business type + Supabase business id resolution.
 */

import { kvGet } from '../infrastructure/storage/kvAccess';
import { readOfflineBusinessesMergedSync } from './authCredentialRead';
import { businessTypes } from './constants';
import { getBusinessTypeId } from './restaurantMode';
import { normalizeBusinessStorageId } from './sharedCore';

/**
 * Strip UI-only fields (Icon, allowedCategories) and resolve labels from canonical config.
 * @param {object|string|null} businessType
 * @returns {{ id: string, name: string, description: string }|null}
 */
export function serializeBusinessTypeForCloud(businessType) {
  const id = getBusinessTypeId(businessType);
  if (!id) return null;

  const config = businessTypes[id] || {};
  const fromForm =
    businessType && typeof businessType === 'object' ? businessType : null;

  return {
    id,
    name: fromForm?.name || config.name || id,
    description: fromForm?.description || config.description || ''
  };
}

function rowMatchesLocalId(b, localId) {
  if (!b || !localId) return false;
  const bid = normalizeBusinessStorageId(b.id);
  const cloud = b.cloudId != null ? String(b.cloudId) : '';
  const cloudNorm = cloud ? normalizeBusinessStorageId(cloud) || cloud : '';
  return (
    bid === localId ||
    cloud === localId ||
    cloudNorm === localId ||
    String(b.id) === localId ||
    String(b.id) === `business_${localId}`
  );
}

/**
 * Prefer authentic cloud businesses.id over local storage slug.
 * Checks session + offline catalog (KV ∪ native LS via authCredentialRead).
 * @param {string|null|undefined} localBusinessId
 * @returns {string|null}
 */
export function resolveSupabaseBusinessId(localBusinessId) {
  const localId = normalizeBusinessStorageId(localBusinessId);
  if (!localId) return null;

  try {
    // 1) Session pair (authoritative while logged in)
    const currentUser = kvGet('currentUser');
    const sessionBiz = currentUser?.business || kvGet('currentBusiness');
    if (sessionBiz) {
      const sessionLocalId = normalizeBusinessStorageId(
        sessionBiz.id || sessionBiz.businessId
      );
      if (
        !sessionLocalId ||
        sessionLocalId === localId ||
        String(sessionBiz.cloudId || '') === localId ||
        normalizeBusinessStorageId(sessionBiz.cloudId) === localId
      ) {
        if (sessionBiz.cloudId) {
          return String(sessionBiz.cloudId);
        }
      }
    }

    // 2) Merged offline catalog (ADR-0002: no raw localStorage here)
    const businesses = readOfflineBusinessesMergedSync();
    const found = businesses.find((b) => rowMatchesLocalId(b, localId));
    if (found?.cloudId) {
      return String(found.cloudId);
    }

    // 3) Caller already passed a known cloud id stored as-is on some row
    const asCloud = businesses.find(
      (b) => b?.cloudId && String(b.cloudId) === String(localBusinessId)
    );
    if (asCloud?.cloudId) {
      return String(asCloud.cloudId);
    }
  } catch {
    /* optional */
  }

  // Fallback: normalized local slug (works when businesses.id was set from meta.business_id)
  return localId;
}
